import React, { useEffect, useState } from 'react';
import { X, Check, Crown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const FEATURES = [
  'Unlimited Ask Clarity AI chat',
  'Full personalised investment recommendations',
  'Monthly check-in & score tracking',
  'Goal tracking & new plans anytime',
];

export default function PremiumModal({ open, onClose }) {
  const [pricing, setPricing] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/pricing').then(({ data }) => setPricing(data)).catch(console.error);
    }
  }, [open]);

  if (!open) return null;

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post('/checkout/session', { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error('Could not start checkout. Please try again.');
      setPaying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="premium-modal" className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button data-testid="premium-modal-close" onClick={onClose} className="absolute top-4 right-4 text-[#A1A1AA] hover:text-[#0A0A0B] p-2">
          <X size={20} />
        </button>

        <div className="w-12 h-12 rounded-2xl bg-[#534AB7] flex items-center justify-center mb-5">
          <Crown size={22} className="text-white" />
        </div>
        <h3 className="text-2xl font-bold tracking-tight text-[#0A0A0B] mb-2">Clarity Premium</h3>

        {pricing ? (
          <div className="mb-5">
            {pricing.is_founding_price ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span data-testid="premium-price" className="text-4xl font-extrabold text-[#0A0A0B]">RM{pricing.founding_price.toFixed(0)}</span>
                  <span className="text-lg text-[#A1A1AA] line-through">RM{pricing.regular_price.toFixed(0)}</span>
                  <span className="text-sm text-[#5C5C66]">/month</span>
                </div>
                <p data-testid="founding-slots" className="text-sm font-semibold text-[#534AB7] mt-1.5">
                  Founding member price — only {pricing.founding_slots_left} of 50 spots left
                </p>
              </>
            ) : (
              <div className="flex items-baseline gap-2">
                <span data-testid="premium-price" className="text-4xl font-extrabold text-[#0A0A0B]">RM{pricing.regular_price.toFixed(0)}</span>
                <span className="text-sm text-[#5C5C66]">/month</span>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-5 text-sm text-[#5C5C66]">Loading pricing...</div>
        )}

        <ul className="space-y-3 mb-7">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm sm:text-base text-[#0A0A0B]">
              <span className="w-5 h-5 rounded-full bg-[#10B981]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check size={13} className="text-[#10B981]" />
              </span>
              {f}
            </li>
          ))}
        </ul>

        <button
          data-testid="premium-pay-btn"
          onClick={pay}
          disabled={paying || !pricing}
          className="w-full bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-8 py-4 font-semibold text-lg disabled:opacity-50 transition-colors"
        >
          {paying ? 'Redirecting to payment...' : 'Pay with FPX / Card'}
        </button>
        <p className="text-xs text-[#A1A1AA] text-center mt-3">Secure payment via Stripe. FPX supported for Malaysian banks. Cancel anytime.</p>
      </div>
    </div>
  );
}
